import ky, { HTTPError, ResponsePromise, SearchParamsOption } from 'ky';
import { Options } from 'ky/distribution/types/options';
import { SERVER_URL } from 'src/config';
import { AuthService } from 'src/api/auth.service';
import { supportModeService } from 'src/services/supportMode.service';

export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'HEAD' | 'DELETE';

type DeserializeCallback = (res: ResponsePromise) => Promise<unknown>;

const defaultDeserialize: DeserializeCallback = (res: ResponsePromise) =>
    res.json() as Promise<unknown>;

const extractErrorMessage = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : undefined;
    }

    if (Array.isArray(value)) {
        const messages = value
            .map(item => extractErrorMessage(item))
            .filter((message): message is string => Boolean(message));

        return messages.length ? messages.join(', ') : undefined;
    }

    if (value && typeof value === 'object') {
        const payload = value as {
            message?: unknown;
            error?: unknown;
        };

        return (
            extractErrorMessage(payload.message) ??
            extractErrorMessage(payload.error)
        );
    }

    return undefined;
};

export abstract class BaseHTTPService {
    ky: typeof ky;
    constructor({ options }: { options: Options }) {
        this.ky = ky.extend(options);
    }

    private normalizeError = async (error: unknown): Promise<Error> => {
        if (error instanceof HTTPError) {
            try {
                const payload = (await error.response.clone().json()) as {
                    message?: unknown;
                    error?: unknown;
                    code?: unknown;
                };
                // Keep the server's stable error code so screens can translate it.
                if (typeof payload.code === 'string')
                    Object.assign(error, { code: payload.code });
                const message =
                    extractErrorMessage(payload.message) ??
                    extractErrorMessage(payload.error);

                if (message) {
                    error.message = message;
                    return error;
                }
            } catch (_e) {}
        }

        if (error instanceof Error) {
            return error;
        }

        return new Error('Unknown request error');
    };

    request = async <T>(
        method: HTTPMethod,
        endpoint: string,
        {
            params,
            payload,
            options,
            deserialize = defaultDeserialize,
            unwrapData = true,
        }: Request,
    ) => {
        try {
            const res = this.ky(endpoint, {
                method,
                searchParams: params,
                json: payload,
                ...options,
            });

            const response = await deserialize(res);
            const responseData = (response as { data?: unknown } | null)?.data;

            if (unwrapData && responseData !== undefined) {
                return responseData as T;
            }

            return response as T;
        } catch (error) {
            throw await this.normalizeError(error);
        }
    };
}

export type RequestOptions = {
    params?: SearchParamsOption;
    payload?: unknown;
    options?: Options;
    deserialize?: DeserializeCallback;
    unwrapData?: boolean;
};

export type Request = {
    params?: SearchParamsOption;
    payload?: unknown;
    options?: Options;
    deserialize?: DeserializeCallback;
    unwrapData?: boolean;
};

export class HTTPService extends BaseHTTPService {
    constructor({ authService }: { authService: AuthService }) {
        super({
            options: {
                hooks: {
                    beforeRequest: [
                        async request => {
                            const token = await authService.getUserToken();
                            if (token) {
                                request.headers.set(
                                    'authorization',
                                    `Bearer ${token}`,
                                );
                            } else {
                                request.headers.delete('authorization');
                            }

                            const supportSession = supportModeService.get();
                            if (supportSession?.token) {
                                request.headers.set(
                                    'x-mundu-support-session',
                                    supportSession.token,
                                );
                            } else {
                                request.headers.delete(
                                    'x-mundu-support-session',
                                );
                            }
                        },
                    ],
                },
                timeout: 25_000,
                headers: {
                    'content-type': 'application/json',
                },
                prefixUrl: SERVER_URL,
                retry: 0,
            },
        });
    }
    get = <T>(endpoint: string, options?: RequestOptions) =>
        this.request<T>('GET', endpoint, { ...options });

    post = <T>(endpoint: string, options?: RequestOptions) =>
        this.request<T>('POST', endpoint, { ...options });

    delete = <T>(endpoint: string, options?: RequestOptions) =>
        this.request<T>('DELETE', endpoint, { ...options });

    patch = <T>(endpoint: string, options?: RequestOptions) =>
        this.request<T>('PATCH', endpoint, { ...options });
}
